import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, ShieldCheck, Lock, KeyRound, Fingerprint, Mail, Database,
  EyeOff, FileLock2, Server, Award, ArrowRight, Globe2,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";

const FEATURES = [
  {
    icon: Lock,
    title: "Authentification renforcée",
    desc: "Mot de passe chiffré avec bcrypt, vérification email obligatoire, 2FA (TOTP) optionnelle, limitation des tentatives de connexion contre la force brute.",
  },
  {
    icon: KeyRound,
    title: "Connexion sécurisée HTTPS",
    desc: "Chiffrement bout-en-bout TLS 1.3. Cookies httpOnly + SameSite Strict + Secure. Sessions courtes avec refresh-token rotatif.",
  },
  {
    icon: Database,
    title: "Données hébergées en France",
    desc: "Toutes tes données (compte, documents, démarches, messages) sont stockées sur des serveurs en France. Aucun transfert hors UE. Conforme RGPD.",
  },
  {
    icon: FileLock2,
    title: "Documents protégés",
    desc: "Tes documents (carte d'identité, RIB, certificats) sont chiffrés au repos et accessibles uniquement par toi via un lien temporaire signé.",
  },
  {
    icon: Mail,
    title: "Vérification email obligatoire",
    desc: "Aucun compte n'est actif tant que l'email n'est pas vérifié. Les liens de réinitialisation sont à usage unique et expirent en 1 heure.",
  },
  {
    icon: EyeOff,
    title: "Anonymat dans la communauté",
    desc: "Dans le chat et le forum, tu n'apparais que sous un pseudo généré aléatoirement. Personne ne voit ton vrai nom, ton email ni ta photo.",
  },
  {
    icon: ShieldCheck,
    title: "Modération bienveillante",
    desc: "Filtre anti-insultes / anti-racisme / anti-LGBTQphobie / anti-discrimination. Signalement à 1 clic. 3 signalements = message auto-masqué pour modération.",
  },
  {
    icon: Fingerprint,
    title: "Droit à l'oubli (RGPD Art. 17)",
    desc: "Tu peux exporter ou supprimer toutes tes données en un clic depuis tes paramètres. La suppression est définitive, sans question, sans rétention.",
  },
];

const FAQ = [
  {
    q: "Mes données peuvent-elles être vendues ou partagées ?",
    a: "Jamais. Student Hub ne vend pas, ne loue pas, ne partage pas tes données avec des tiers à des fins commerciales. Nous n'utilisons pas de tracker publicitaire. La seule monétisation est l'abonnement Premium (2,99 €/mois) — pas la donnée.",
  },
  {
    q: "Que se passe-t-il si je ferme mon compte ?",
    a: "La suppression de compte efface : ton profil, tes documents, ton historique de démarches, tes messages communautaires (chat + forum), tes notifications. Toutes les sauvegardes sont purgées sous 30 jours. Aucune donnée résiduelle n'est conservée à des fins marketing.",
  },
  {
    q: "Comment puis-je signaler un message problématique ?",
    a: "Sur chaque message du chat ou du forum, le bouton « Signaler » est visible. Le signalement est anonyme. Le message est automatiquement masqué après 3 signalements indépendants en attendant l'examen.",
  },
  {
    q: "Pouvez-vous lire mes messages communautaires ?",
    a: "L'équipe modération peut consulter les messages signalés (uniquement ceux-là) pour traiter les abus. Aucune lecture systématique. Le contenu n'est jamais utilisé pour entraîner une IA.",
  },
  {
    q: "Que se passe-t-il en cas de fuite de données ?",
    a: "Conformément au RGPD (Art. 33), nous notifierions la CNIL sous 72 heures et chaque utilisateur impacté. Cependant, mots de passe et documents sont chiffrés — même en cas d'intrusion, ils restent illisibles.",
  },
];

export default function Securite() {
  const canonical = "https://studenthubfrance.fr/securite";
  const title = "Sécurité & confidentialité — Student Hub France";
  const description = "Comment Student Hub protège ta vie privée : chiffrement, RGPD, 2FA, données hébergées en France, anonymat communautaire, droit à l'oubli.";

  useSeo({
    title,
    description,
    canonical,
    lang: "fr",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  });

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" data-testid="page-securite">
      {/* HEADER */}
      <header className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between border-b border-slate-100">
        <Link to="/" className="inline-flex items-center gap-2 group" data-testid="securite-logo">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-600/20 group-hover:scale-105 transition-transform">
            <GraduationCap size={20} className="text-white" />
          </div>
          <span className="font-display font-bold text-lg">Student Hub</span>
        </Link>
        <nav className="flex items-center gap-3 text-sm">
          <Link to="/legal/confidentialite" className="hidden sm:inline text-slate-500 hover:text-slate-900">Politique de confidentialité</Link>
          <Link to="/login" className="text-slate-600 hover:text-slate-900">Se connecter</Link>
        </nav>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        {/* HERO */}
        <section className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 mb-5">
            <ShieldCheck size={14} className="text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700 tracking-wide">Sécurité & vie privée by design</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            Tes données sont <span className="bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">en sécurité.</span> Ton anonymat aussi.
          </h1>
          <p className="text-slate-600 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
            Tu nous confies des choses importantes : carte d'identité, RIB, démarches CAF, conversations sensibles. On prend ça très au sérieux. Voici exactement comment Student Hub te protège.
          </p>
          <div className="mt-8 flex flex-wrap gap-2 justify-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-800">🔒 HTTPS · TLS 1.3</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-800">🇪🇺 RGPD compliant</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-xs font-semibold text-violet-800">🇫🇷 Données en France</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-800"><Award size={12} /> Aucun tracker pub</span>
          </div>
        </section>

        {/* FEATURES grid */}
        <section className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="securite-features">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-300 hover:shadow-md transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-500 text-white flex items-center justify-center mb-4 shadow-md shadow-emerald-500/20">
                  <Icon size={20} />
                </div>
                <h2 className="font-display font-semibold text-slate-900 text-base mb-1.5">{f.title}</h2>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </section>

        {/* TECHNICAL DETAILS */}
        <section className="mt-16 rounded-3xl bg-slate-900 text-white p-8 sm:p-12">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center shrink-0">
              <Server size={22} className="text-emerald-300" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold">Pour les techniques curieux·euses</h2>
              <p className="text-slate-300 mt-2 text-sm leading-relaxed max-w-2xl">
                Quelques détails pour ceux et celles qui veulent voir sous le capot — sans jargon inutile.
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-200 leading-relaxed">
                <li>• Mots de passe stockés avec <strong className="text-white">bcrypt</strong> (12 rounds) — jamais en clair, jamais dans les logs.</li>
                <li>• Authentification <strong className="text-white">JWT</strong> + refresh token rotatif, cookies <code className="text-emerald-300">httpOnly + Secure + SameSite=Strict</code>.</li>
                <li>• Rate limiting (slowapi) sur tous les endpoints sensibles : login, register, reset password, send message.</li>
                <li>• Protection XSS (sanitization input + CSP headers) et SQL/NoSQL injection (driver Motor + Pydantic validation stricte).</li>
                <li>• Uploads documents : signature de type MIME + limite de taille + scan antivirus (en cours de déploiement).</li>
                <li>• Variables secrètes (clés API, JWT secret) en variables d'environnement — jamais commitées dans GitHub.</li>
                <li>• Stripe & PayPal en mode séparé : aucune donnée carte bancaire ne transite par nos serveurs.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-16" id="faq">
          <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">Questions fréquentes</h2>
          <p className="text-slate-600 mb-8">Si une question n'est pas listée ici, contacte-nous : <a href="mailto:contact@studenthubfrance.fr" className="text-blue-600 hover:underline">contact@studenthubfrance.fr</a></p>
          <div className="space-y-3" data-testid="securite-faq">
            {FAQ.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-emerald-300 transition-colors">
                <summary className="font-semibold text-slate-900 cursor-pointer flex items-center justify-between">
                  {f.q}
                  <span className="text-emerald-500 transition-transform group-open:rotate-180">⌄</span>
                </summary>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-16 text-center">
          <div className="rounded-3xl bg-gradient-to-r from-emerald-600 to-blue-600 px-8 sm:px-16 py-14 text-white">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Prêt·e à essayer en toute confiance ?</h2>
            <p className="mt-3 text-emerald-50 max-w-xl mx-auto">Compte gratuit · Suppression en 1 clic · Aucune carte bancaire pour démarrer.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors shadow-xl">
                Créer mon compte gratuit <ArrowRight size={16} />
              </Link>
              <Link to="/legal/confidentialite" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/30 font-medium transition-colors">
                Lire la politique complète
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-100 py-8 mt-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© 2026 Student Hub · Made in France 🇫🇷 · Hébergé en France</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/legal/cgu" className="hover:text-slate-900">CGU</Link>
            <Link to="/legal/confidentialite" className="hover:text-slate-900">Confidentialité</Link>
            <Link to="/legal/mentions" className="hover:text-slate-900">Mentions légales</Link>
            <Link to="/legal/cookies" className="hover:text-slate-900">Cookies</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
